import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  type ButtonProps,
  Dialog,
  type DialogProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Box,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { InfoCircle } from "iconsax-react";
import BDrawer from "../BDrawer";

interface CompactDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  children?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: "primary" | "error" | "warning" | "inherit";
  confirmFirst?: boolean;
  showIcon?: boolean;
  iconColor?: "primary" | "error" | "warning" | "inherit";
  iconVariant?: "Outline" | "Bulk";
  iconSize?: number;
  showCloseButton?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  cancelButtonProps?: Partial<ButtonProps>;
  confirmButtonProps?: Partial<ButtonProps>;
  paperSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  titleTextSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  messageTextSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  closeButtonSx?: SxProps<Theme>;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Compact confirmation popup matching the Figma "Popup" component
 * (https://www.figma.com/design/wFbBeKBPOxRiuNGFdDpl7O/BestekAI?node-id=3273-10992):
 * tight 12px/8px padded neutral card, 28px header icon, 12px gap between sections.
 * For the more spacious generic dialog spec, use ConfirmationDialog instead.
 */
const CompactDialog: FC<CompactDialogProps> = ({
  open,
  title,
  message,
  children,
  confirmText,
  cancelText,
  confirmColor = "inherit",
  confirmFirst = false,
  showIcon = true,
  iconColor,
  iconVariant = "Outline",
  iconSize = 28,
  showCloseButton = true,
  maxWidth = "xs",
  cancelButtonProps,
  confirmButtonProps,
  paperSx,
  titleSx,
  titleTextSx,
  contentSx,
  messageTextSx,
  actionsSx,
  closeButtonSx,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1200));
  const resolvedConfirmText = confirmText ?? t("common.confirm");
  const resolvedCancelText = cancelText ?? t("common.cancel");

  const resolvedIconColor = (iconColor ?? confirmColor) === "error"
    ? theme.palette.error.main
    : (iconColor ?? confirmColor) === "warning"
    ? theme.palette.warning.main
    : (iconColor ?? confirmColor) === "primary"
    ? theme.palette.primary.main
    : theme.palette.primary.main;

  const cancelButton = (
    <Button key="cancel" fullWidth onClick={onCancel} variant="outlined" color="inherit" {...cancelButtonProps}>
      {resolvedCancelText}
    </Button>
  );

  const confirmButton = (
    <Button
      key="confirm"
      fullWidth
      onClick={onConfirm}
      variant="contained"
      color={confirmColor}
      {...confirmButtonProps}
    >
      {resolvedConfirmText}
    </Button>
  );

  const actions = confirmFirst ? (
    <>
      {confirmButton}
      {cancelButton}
    </>
  ) : (
    <>
      {cancelButton}
      {confirmButton}
    </>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {message && (
        <Typography variant="body2" sx={{ color: "text.primary", ...messageTextSx }}>
          {message}
        </Typography>
      )}
      {children}
    </Box>
  );

  if (isMobile) {
    return (
      <BDrawer open={open} onClose={onCancel} title={title} dialogActions={actions} showCloseButton>
        {content}
      </BDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "background.neutral",
          borderRadius: "12px",
          boxShadow: "none",
          p: 0,
          ...paperSx,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          px: "12px",
          pt: "8px",
          pb: 0,
          ...titleSx,
        }}
      >
        {showIcon && (
          <InfoCircle size={iconSize} variant={iconVariant} color={resolvedIconColor} style={{ flexShrink: 0 }} />
        )}
        <Box
          component="span"
          sx={{
            flex: 1,
            fontFamily: "Archivo",
            fontSize: "20px",
            fontWeight: 400,
            lineHeight: "30px",
            color: "text.primary",
            ...titleTextSx,
          }}
        >
          {title}
        </Box>
        {showCloseButton && (
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{
              flexShrink: 0,
              width: "36px",
              height: "36px",
              color: theme.palette.text.secondary,
              ...closeButtonSx,
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: "12px", pt: "12px", pb: 0, ...contentSx }}>{content}</DialogContent>

      <DialogActions sx={{ px: "12px", pt: "12px", pb: "8px", gap: "8px", ...actionsSx }}>{actions}</DialogActions>
    </Dialog>
  );
};

export default CompactDialog;
